import { useId, useState } from "react";
import { useDisclosure } from "@chakra-ui/hooks";
import { MdAdd, MdEdit, MdUpload } from "react-icons/md";
import InputField from "components/fields/InputField";
import Select from "components/fields/Select";
import Checkbox from "components/fields/Checkbox";
import DatePicker from "components/fields/DatePicker";
import Button from "components/button";
import Dialog from "components/dialog";
import Avatar from "components/avatar";

const AllergiesArray = [
  "Milk",
  "Eggs",
  "Peanuts",
  "Tree nuts (e.g., almonds, walnuts)",
  "Wheat",
  "Soy",
  "Fish",
  "Shellfish (e.g., shrimp, crab)",
  "Sesame",
  "Mustard",
];

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Non-binary" },
  { value: "na", label: "Prefer not to tell" },
];

const GRADE_OPTIONS = [
  { value: "1", label: "Preschool" },
  { value: "2", label: "Pre-kindergarten" },
  { value: "3", label: "Kindergarten" },
  { value: "4", label: "1st Grade" },
  { value: "5", label: "2nd Grade" },
  { value: "6", label: "3rd Grade" },
  { value: "7", label: "4th Grade" },
  { value: "8", label: "5th Grade" },
  { value: "9", label: "6th Grade" },
  { value: "10", label: "7th Grade" },
  { value: "11", label: "8th Grade" },
];

// The register API expects dates as MM/DD/YYYY.
function formatToMMDDYYYY(date) {
  if (!date) return "";
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Child details form shared by check-in (add guest) and registration.
 * `onSubmit(user)` may return a promise; the submit button shows a spinner until it settles.
 */
const NewChildForm = ({ onSubmit, errors }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const photoInputId = useId();

  const [user, setUser] = useState({});
  const [avatar, setAvatar] = useState();
  const [saving, setSaving] = useState(false);

  const fieldState = (path) =>
    errors.some((error) => error.path === path) ? "error" : undefined;

  const allergies = user.allergies || [];

  const handleFileChange = (e) => {
    if (e.target.files) {
      setAvatar(e.target.files[0]);
    }
  };

  const toggleAllergy = (k, checked) => {
    const rest = allergies.filter((a) => a !== k);
    setUser({ ...user, allergies: checked ? rest.concat([k]) : rest });
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    const payload = avatar
      ? { ...user, role: "child", avatar: avatar }
      : { ...user, role: "child" };
    setSaving(true);
    Promise.resolve(onSubmit(payload)).finally(() => setSaving(false));
  };

  return (
    <>
      <form onSubmit={onFormSubmit} noValidate={true} className="flex flex-col gap-6">
        <section className="flex flex-col gap-4">
          <h2 className="text-heading">Child Details</h2>
          <InputField
            label="First Name"
            placeholder="Child first name"
            value={user.firstName || ""}
            onChange={(e) => setUser({ ...user, firstName: e.target.value })}
            state={fieldState("firstName")}
          />
          <InputField
            label="Last Name"
            placeholder="Child last name"
            value={user.lastName || ""}
            onChange={(e) => setUser({ ...user, lastName: e.target.value })}
            state={fieldState("lastName")}
          />
          <Select
            label="Gender"
            placeholder="Select gender"
            value={user.gender || ""}
            onChange={(e) => setUser({ ...user, gender: e.target.value })}
            state={fieldState("gender")}
            options={GENDER_OPTIONS}
            required
          />
          <DatePicker
            label="Date of Birth"
            placeholder="Date of birth"
            value={formatToMMDDYYYY(user.dob)}
            onChange={(v) => {
              setUser({ ...user, dob: formatToMMDDYYYY(v) });
            }}
            state={fieldState("dob")}
          />
          <Select
            label="Grade"
            placeholder="Select grade"
            value={user.grade || ""}
            onChange={(e) => setUser({ ...user, grade: e.target.value })}
            state={fieldState("grade")}
            options={GRADE_OPTIONS}
            required
          />
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium text-fg">Profile Photo</h3>
          <div className="flex items-center gap-3">
            {avatar ? (
              <Avatar
                src={URL.createObjectURL(avatar)}
                name={`${user.firstName || ""} ${user.lastName || ""}`}
                size="xl"
              />
            ) : (
              user.thumb && <Avatar user={user} />
            )}
            <Button
              as="label"
              htmlFor={photoInputId}
              variant="secondary"
              leftIcon={<MdUpload aria-hidden="true" className="h-5 w-5" />}
              className="cursor-pointer focus-within:ring-2 focus-within:ring-focus focus-within:ring-offset-2 focus-within:ring-offset-surface"
            >
              Upload Photo
              <input
                id={photoInputId}
                className="sr-only"
                type="file"
                onChange={handleFileChange}
                accept="image/*"
              />
            </Button>
          </div>
          {fieldState("avatar") && (
            <p className="text-xs text-danger-fg">Upload a valid photo.</p>
          )}
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium text-fg">Food Allergies</h3>
          {allergies.length > 0 && (
            <p className="rounded-control border border-line bg-surface p-3 text-sm">
              {allergies
                .map((k) => AllergiesArray[k])
                .filter((x) => x)
                .join(", ")}
            </p>
          )}
          <div>
            <Button
              variant="secondary"
              leftIcon={
                allergies.length === 0 ? (
                  <MdAdd aria-hidden="true" className="h-5 w-5" />
                ) : (
                  <MdEdit aria-hidden="true" className="h-5 w-5" />
                )
              }
              onClick={onOpen}
            >
              {allergies.length === 0 ? "Add Allergies" : "Edit Allergies"}
            </Button>
          </div>
        </section>

        <Button type="submit" size="lg" fullWidth loading={saving}>
          Add Child
        </Button>
      </form>

      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title="Food Allergies"
        description="Select any food allergies for this child."
        footer={<Button onClick={onClose}>Done</Button>}
      >
        <div className="flex flex-col gap-3">
          {AllergiesArray.map((tag, k) => (
            <Checkbox
              key={k}
              label={tag}
              checked={allergies.indexOf(k) !== -1}
              onChange={(e) => toggleAllergy(k, e.target.checked)}
            />
          ))}
        </div>
      </Dialog>
    </>
  );
};

NewChildForm.AllergiesArray = AllergiesArray;

export default NewChildForm;
